import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import { formatDate } from '../utils/formatDate';
import OffenseForm from './OffenseForm';
import AssociatedPersons from './AssociatedPersons'; // <-- Import the new component

function Offenses({ juvenileId }) {
  const [offenses, setOffenses] = useState([]);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formError, setFormError] = useState('');
  const { hasPermission } = useContext(AuthContext);
  const [expandedOffenseId, setExpandedOffenseId] = useState(null); // <-- State to track expanded offense

  const fetchOffenses = useCallback(async () => {
    if (!juvenileId) return;
    try {
      const response = await apiClient.get(`/api/juveniles/${juvenileId}/offenses`);
      setOffenses(response.data.offenses || []);
    } catch (err) {
      setError('Failed to fetch offenses.');
      console.error(err);
    }
  }, [juvenileId]);

  useEffect(() => {
    fetchOffenses();
  }, [fetchOffenses]);

  const handleAddSubmit = async (formData) => {
    try {
      setFormError('');
      await apiClient.post(`/api/juveniles/${juvenileId}/offenses`, formData);
      setShowAddForm(false);
      fetchOffenses();
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to add offense.");
    }
  };
  
  const toggleAssociates = (offenseId) => {
      setExpandedOffenseId(prevId => (prevId === offenseId ? null : offenseId));
  };

  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-slate-800 mb-0">Offense History</h2>
        {hasPermission('create-offense') && !showAddForm && (
          <button onClick={() => setShowAddForm(true)} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">
            Add New Offense
          </button>
        )}
      </div>

      {showAddForm && <OffenseForm onSubmit={handleAddSubmit} onCancel={() => setShowAddForm(false)} error={formError} />}

      <div className="mt-4 space-y-4">
        {offenses.length > 0 ? (
          offenses.map(offense => (
            <div key={offense.id} className="p-4 border rounded-lg bg-slate-50">
              <div className="flex justify-between items-start">
                <div>
                    <h4 className="font-bold text-lg text-slate-700">{offense.offense_type}</h4>
                    <p className="text-sm text-slate-500">Case: {offense.case_number} | Arrested: {formatDate(offense.arrest_date)}</p>
                </div>
                <button onClick={() => toggleAssociates(offense.id)} className="text-sm text-slate-600 hover:text-gold">
                  {expandedOffenseId === offense.id ? 'Hide Associates' : 'Manage Associates'}
                </button>
              </div>
              {/* Conditionally render the AssociatedPersons component */}
              {expandedOffenseId === offense.id && <AssociatedPersons offenseId={offense.id} />}
            </div>
          ))
        ) : (
          !showAddForm && <p>No offenses recorded for this juvenile.</p>
        )}
      </div>
    </div>
  );
}

export default Offenses;